import React, { useEffect, useRef, useState } from 'react'
import StatCard from './stat-card'

export interface StatItem {
  value: string;
  label: string;
  sublabel?: string;
  icon?: string;
  imageUrl?: string;
}

export interface MinistryStatsProps {
  title: string;
  description?: string;
  statistics: StatItem[];
  verticalAlignment?: 'start' | 'center' | 'end';
}

// Define our limited palette colors
const allowedColors = [
  'rg-green',
  'yellow'
]

const MinistryStats: React.FC<MinistryStatsProps> = ({
  title,
  description,
  statistics,
  verticalAlignment,
}) => {
  const statsRef = useRef<HTMLDivElement>(null)
  const [cardColors, setCardColors] = useState<string[]>([])


  // Generate consistent cycling colors on component mount
  useEffect(() => {
    const cycledColors: string[] = []
    
    // Assign colors in a consistent cycling pattern
    for (let i = 0; i < statistics.length; i++) {
      cycledColors.push(allowedColors[i % allowedColors.length])
    }
    
    setCardColors(cycledColors)
  }, [statistics.length])

  useEffect(() => {
    // Add intersection observer for animation on scroll
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry, index) => {
          if (entry.isIntersecting) {
            // Add a staggered delay based on the card's index
            setTimeout(() => {
              entry.target.classList.add('is-visible')
            }, index * 300) // Increased to 300ms staggered delay between each card
            
            observer.unobserve(entry.target)
          }
        })
      },
      { threshold: 0.15, rootMargin: '0px 0px -50px 0px' }
    )

    if (statsRef.current) {
      const statCards = statsRef.current.querySelectorAll('.stat-card-container')
      statCards.forEach(card => observer.observe(card))
    }

    return () => {
      if (statsRef.current) {
        const statCards = statsRef.current.querySelectorAll('.stat-card-container')
        statCards.forEach(card => observer.unobserve(card))
      }
    }
  }, [])

  // Function to render title with highlighted word
  const renderTitle = () => {
    return <h2 className="section-title">{title}</h2>
  }

  return (
    <div className="ministry-stats-container">
      <div className="container">
        <div className="row align-items-start">
          {/* Left column with text content */}
          <div className={`col-lg-6 content-column justify-content-${verticalAlignment}`}>
            {renderTitle()}
            
            {description && (
              <div className="description-wrapper">
                <p className="lead">{description}</p>
              </div>
            )}
          </div>
          
          {/* Right column with stat cards */}
          <div className="col-lg-6">
            <div className="stats-grid" ref={statsRef}>
              {statistics.map((stat, index) => (
                <div key={index} className="stat-card-container">
                  <StatCard 
                    value={stat.value} 
                    label={stat.label}
                    sublabel={stat.sublabel}
                    icon={stat.icon}
                    imageUrl={stat.imageUrl}
                    delay={index * 100}
                    color={cardColors[index]} // Pass cycled color
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default MinistryStats